/**
 * ADR Withholding-Tax + IRA suitability card (Stock Modal Insights tab and
 * the stock SEO page). Oct 2026 — reads `/api/stocks/:t/adr-withholding`,
 * the same per-row data as the /foreign-dividend-stocks hub (treaty vs
 * at-source rate, reclaim flag, country-specific IRA reason), replacing the
 * old static country-code table that had drifted from the hub's numbers.
 * Self-hides for non-ADRs and for ADRs whose issuer country is unknown.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe, AlertCircle } from 'lucide-react';
import { ccToFlag } from '@/lib/flagEmoji';
import AbbrTip from '@/components/AbbrTip';
import AdrIraSuitability, { IraBadge } from '@/components/AdrIraSuitability';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => (n == null || !isFinite(n) ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (n) => (typeof n === 'number' ? `${Number(n).toLocaleString('en-US', { maximumFractionDigits: 3 })}%` : '—');

function Tile({ label, value, tone = '', sub, testId }) {
  return (
    <div className={`rounded border p-2 ${tone || 'border-slate-200/60 dark:border-slate-700/40 bg-slate-50/40 dark:bg-slate-900/20'}`} data-testid={testId}>
      <div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="font-mono font-bold text-base">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground leading-snug">{sub}</div>}
    </div>
  );
}

export default function AdrWhtEstimatorCard({ stock, userShares, testIdPrefix = 'insights-adr' }) {
  const ticker = stock?.ticker;
  const isAdr = !!stock?.is_adr;
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!ticker || !isAdr) return undefined;
    let cancelled = false;
    axios.get(`${API}/stocks/${encodeURIComponent(ticker)}/adr-withholding`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticker, isAdr]);

  if (!isAdr || !data?.is_adr || !data.country || typeof data.withholding_rate !== 'number') return null;

  const treaty = data.withholding_rate;
  const source = typeof data.withholding_source_rate === 'number' ? data.withholding_source_rate : treaty;
  const divRate = Number(data.dividend_rate || stock.dividend_rate || 0);
  const shares = Number(userShares || 0);
  const whtPerShare = divRate * (treaty / 100);
  const netPerShare = divRate - whtPerShare;
  const cc = (data.country_code || stock.country_code || '').toUpperCase();

  return (
    <div className="rounded-lg border bg-card p-3 space-y-3" data-testid={`${testIdPrefix}-wht-card`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide">
          <Globe className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
          <span>ADR <AbbrTip term="WHT">Withholding</AbbrTip> &amp; IRA fit</span>
          <IraBadge status={data.ira_warning} note={data.ira_note} testId={`${testIdPrefix}-header-ira-badge`} />
        </div>
        <span className="text-[10px] text-muted-foreground" data-testid={`${testIdPrefix}-country`}>
          {cc && ccToFlag(cc)} {data.country}{data.region ? ` · ${data.region}` : ''}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <Tile
          label="Withheld at source"
          value={pct(source)}
          tone={source >= 25 ? 'border-rose-200/60 dark:border-rose-700/40 bg-rose-50/40 dark:bg-rose-950/20' : source > 0 ? 'border-amber-200/60 dark:border-amber-700/40 bg-amber-50/40 dark:bg-amber-950/20' : 'border-emerald-200/60 dark:border-emerald-700/40 bg-emerald-50/40 dark:bg-emerald-950/20'}
          sub={data.withholding_reclaim ? `Treaty rate ${pct(treaty)} — excess reclaimable` : null}
          testId={`${testIdPrefix}-source-rate`}
        />
        <Tile label={<><AbbrTip term="TREATY_RATE">Treaty rate</AbbrTip></>} value={pct(treaty)} sub="What a US taxable holder nets to after reclaim" testId={`${testIdPrefix}-treaty-rate`} />
        <Tile label="Gross yield" value={pct(data.dividend_yield)} testId={`${testIdPrefix}-gross-yield`} />
        <Tile label="Net yield" value={pct(data.net_yield)} sub="After treaty withholding" tone="border-emerald-200/60 dark:border-emerald-700/40 bg-emerald-50/40 dark:bg-emerald-950/20" testId={`${testIdPrefix}-net-yield`} />
      </div>
      {data.withholding_note && (
        <p className="text-[11px] text-muted-foreground leading-snug" data-testid={`${testIdPrefix}-country-note`}>
          <span className="font-semibold text-foreground">{data.country}:</span> {data.withholding_note}
        </p>
      )}

      {divRate > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3 text-sm">
          <Tile label="Gross / share" value={money(divRate)} testId={`${testIdPrefix}-gross-share`} />
          <Tile label={<>Withheld {pct(treaty)}</>} value={treaty > 0 ? `−${money(whtPerShare)}` : money(0)} tone="border-amber-200/60 dark:border-amber-700/40 bg-amber-50/40 dark:bg-amber-950/20" testId={`${testIdPrefix}-wht-share`} />
          <Tile label="Net / share" value={money(netPerShare)} testId={`${testIdPrefix}-net-share`} />
        </div>
      )}

      {shares > 0 && divRate > 0 && (
        <div className="rounded-md bg-muted/40 p-2 text-sm" data-testid={`${testIdPrefix}-wht-user-row`}>
          <div className="flex items-center justify-between text-[11px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
            <span>Your {shares.toLocaleString()} shares · Annual</span>
            <span className="normal-case font-normal"><AbbrTip term="FTC">FTC</AbbrTip> may offset US tax bill</span>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div><div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide">Gross</div><div className="font-mono font-bold">{money(shares * divRate)}</div></div>
            <div><div className="text-[9px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-wide">Withheld</div><div className="font-mono font-bold text-amber-700 dark:text-amber-300">{treaty > 0 ? `−${money(shares * whtPerShare)}` : money(0)}</div></div>
            <div><div className="text-[9px] font-bold text-muted-foreground uppercase tracking-wide">You receive</div><div className="font-mono font-bold">{money(shares * netPerShare)}</div></div>
          </div>
          {treaty > 0 && (
            <div className="flex items-start gap-1.5 mt-2 text-[10px] text-muted-foreground leading-snug">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>Up to {money(shares * whtPerShare)} qualifies for the IRS <AbbrTip term="FTC">Foreign Tax Credit</AbbrTip> on Form 1116 — in a taxable account only. Treaty rates assume a US resident with a W-8BEN on file via DTC; broker treatment can vary. Not tax advice.</span>
            </div>
          )}
        </div>
      )}

      <AdrIraSuitability data={data} testIdPrefix={`${testIdPrefix}-ira`} />
    </div>
  );
}
